import React, { useEffect, useReducer } from "react";
import { Link, useNavigate } from "react-router-dom";
import pluralize from "pluralize";

import Button from "ui/Button";
import Callout from "ui/Callout";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import SectionHeading from "ui/SectionHeading";
import SingleSelect from "ui/SingleSelect";
import {
  Email,
  IncidentIoLogo,
  PagerDutyLogo,
  SlackLogo,
  WebhookIcon,
} from "ui/Icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { useRoutes } from "utils/routes";
import { useGoBackOr } from "utils/hooks";
import { sameSet } from "utils/array";
import { useCurrentUser } from "providers/WithCurrentOrganization";
import { useFeature } from "providers/OrganizationFeatures";
import {
  useAsyncActionFlash,
  useSetNextFlashLocation,
} from "providers/WithFlashes";

import {
  useAlertDestinationFormOptionsQuery,
  useAlertDestinationFormDetailsQuery,
  AlertDestinationFormDetailsQuery,
} from "./gql/Query.generated";
import {
  useCreateAlertDestinationMutation,
  useUpdateAlertDestinationMutation,
  useDeleteAlertDestinationMutation,
  useLeaveAlertDestinationMutation,
} from "./gql/Mutation.generated";

type TargetKind = "slack" | "pagerduty" | "email" | "webhook" | "incident_io";

type Target = {
  kind: TargetKind;
  optionId: string;
  optionLabel: string;
  // Disabled webhooks and alert sources are still selectable, since they can be
  // turned back on
  disabled?: boolean;
};

type Option = { id: string; label: string; disabled?: boolean };

type Descriptor = {
  kind: TargetKind;
  label: string;
  targetLabel: string;
  organizationIntegrationId?: string;
  options: Option[];
};

// An alert rule that routes to this destination (shown read-only on the detail).
type TargetRule = NonNullable<
  AlertDestinationFormDetailsQuery["getAlertDestinationDetails"]
>["rules"][number];

type AlertDestinationFormData = {
  descriptors: Descriptor[];
  initialName: string;
  initialTargets: Target[];
  rules: TargetRule[];
  isDefault: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

type FormAction =
  | { type: "rename"; value: string }
  | { type: "add"; target: Target }
  | { type: "remove"; target: Target };

const KIND_ICONS: Record<TargetKind, React.ComponentType> = {
  slack: SlackLogo,
  pagerduty: PagerDutyLogo,
  email: Email,
  webhook: WebhookIcon,
  incident_io: IncidentIoLogo,
};

// Kinds whose targets are integration endpoints managed on the Integrations
// page, rather than picked from a connection
const ENDPOINT_KINDS: TargetKind[] = ["webhook", "incident_io"];

// One section per kind, in display order. `noun` is used for the count summary
// ("13 members").
const SECTIONS: {
  kind: TargetKind;
  label: string;
  noun: string;
}[] = [
  { kind: "email", label: "E-mail", noun: "member" },
  { kind: "slack", label: "Slack", noun: "channel" },
  { kind: "pagerduty", label: "PagerDuty", noun: "service" },
  { kind: "webhook", label: "Webhooks", noun: "endpoint" },
  { kind: "incident_io", label: "incident.io", noun: "alert source" },
];

// Destination mutations change names/labels shown by the list and detail queries,
// so refetch those after any change to avoid stale data on the page we return to.
const REFETCH_QUERIES = ["AlertDestinationList", "AlertDestinationFormDetails"];

// Standalone page host: the destination form wrapped in the usual Panel chrome,
// with its own "Removal" panel. Saving navigates to the destinations list;
// Cancel goes back.
function AlertDestinationDetail({
  organizationSlug,
  destinationId,
}: {
  organizationSlug: string;
  destinationId?: string;
}) {
  const navigate = useNavigate();
  const setNextFlashLocation = useSetNextFlashLocation();
  const { organizationAlertDestinations } = useRoutes();
  const goBack = useGoBackOr(organizationAlertDestinations(organizationSlug));
  const { error, isEdit, form } = useAlertDestinationFormData(
    organizationSlug,
    destinationId,
  );

  if (!form) {
    return <Loading error={error} />;
  }

  const editingId = isEdit ? destinationId : undefined;
  const toList = () => {
    const to = organizationAlertDestinations(organizationSlug);
    setNextFlashLocation(to);
    navigate(to);
  };

  return (
    <>
      <Panel
        title={isEdit ? "Edit Alert Destination" : "Add Alert Destination"}
      >
        <AlertDestinationForm
          key={destinationId ?? "new"}
          organizationSlug={organizationSlug}
          destinationId={editingId}
          initialName={form.initialName}
          initialTargets={form.initialTargets}
          rules={form.rules}
          isDefault={form.isDefault}
          descriptors={form.descriptors}
          canEdit={form.canEdit}
          linkRules
          onCreated={toList}
          onSaved={toList}
          onCancel={goBack}
        />
      </Panel>
      {form.canDelete && editingId && (
        <DeletePanel
          destinationId={editingId}
          ruleCount={form.rules.length}
          onDeleted={toList}
        />
      )}
    </>
  );
}

// Modal host (opened from the alert-rule editor): the bare form, no Panel or
// delete. Instead of navigating on save, it hands control back to the opener via
// callbacks, and rule references are plain text so a click can't discard the
// opener's in-progress edits.
export function AlertDestinationDetailModal({
  organizationSlug,
  destinationId,
  onCreated,
  onSaved,
  onCancel,
}: {
  organizationSlug: string;
  destinationId?: string;
  onCreated: (destination: { id: string; name: string }) => void;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { error, isEdit, form } = useAlertDestinationFormData(
    organizationSlug,
    destinationId,
  );

  if (!form) {
    return <Loading error={error} />;
  }

  return (
    <AlertDestinationForm
      key={destinationId ?? "new"}
      organizationSlug={organizationSlug}
      destinationId={isEdit ? destinationId : undefined}
      initialName={form.initialName}
      initialTargets={form.initialTargets}
      rules={form.rules}
      isDefault={form.isDefault}
      descriptors={form.descriptors}
      canEdit={form.canEdit}
      linkRules={false}
      onCreated={onCreated}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  );
}

// Loads and shapes everything the form needs, independent of the host. Returns a
// null `form` while loading or on error.
function useAlertDestinationFormData(
  organizationSlug: string,
  destinationId?: string,
): {
  error: boolean;
  isEdit: boolean;
  form: AlertDestinationFormData | null;
} {
  const isEdit = !!destinationId && destinationId !== "new";
  const hasWebhooksFeature = useFeature("webhooks");
  const hasIncidentIoFeature = useFeature("incidentIo");
  // Options (picker sources) are always needed; the destination itself only when
  // editing, so that query is skipped in the create flow.
  const optionsQuery = useAlertDestinationFormOptionsQuery({
    variables: { organizationSlug },
  });
  const detailsQuery = useAlertDestinationFormDetailsQuery({
    variables: { destinationId: destinationId ?? "" },
    skip: !isEdit,
  });

  const error = !!optionsQuery.error || !!detailsQuery.error;
  if (error || !optionsQuery.data || (isEdit && !detailsQuery.data)) {
    return { error, isEdit, form: null };
  }

  const org = optionsQuery.data.getOrganizationDetails;
  const descriptors: Descriptor[] = [];
  if (org.slackIntegration) {
    descriptors.push({
      kind: "slack",
      label: "Slack",
      targetLabel: "Channel",
      organizationIntegrationId: org.slackIntegration.id,
      options: (org.slackIntegration.availableChannels.channels ?? []).map(
        (c) => ({ id: c.id, label: c.name }),
      ),
    });
  }
  if (org.pagerdutyIntegration) {
    descriptors.push({
      kind: "pagerduty",
      label: "PagerDuty",
      targetLabel: "Service",
      organizationIntegrationId: org.pagerdutyIntegration.id,
      options: (org.pagerdutyIntegration.availableServices ?? []).map((s) => ({
        id: s.id,
        label: s.name,
      })),
    });
  }
  descriptors.push({
    kind: "email",
    label: "E-mail",
    targetLabel: "Member",
    options: org.memberships.map((m) => ({ id: m.id, label: m.email })),
  });
  // Also shown without the feature flag when endpoints already exist, so their
  // targets stay visible and removable
  if (hasWebhooksFeature || org.webhookEndpoints.length > 0) {
    descriptors.push({
      kind: "webhook",
      label: "Webhooks",
      targetLabel: "Endpoint",
      options: org.webhookEndpoints.map((e) => ({
        id: e.id,
        label: e.name,
        disabled: !!e.disabledAt,
      })),
    });
  }
  if (hasIncidentIoFeature || org.incidentIoEndpoints.length > 0) {
    descriptors.push({
      kind: "incident_io",
      label: "incident.io",
      targetLabel: "Alert source",
      options: org.incidentIoEndpoints.map((e) => ({
        id: e.id,
        label: e.name,
        disabled: !!e.disabledAt,
      })),
    });
  }

  const detail = detailsQuery.data?.getAlertDestinationDetails;
  const initialTargets: Target[] = detail
    ? [
        ...detail.integrations.map((i) => {
          const endpoint = i.webhookEndpoint ?? i.incidentIoEndpoint;
          return {
            kind: i.kind as TargetKind,
            optionId: endpoint?.id ?? i.targetId ?? "",
            optionLabel: i.targetLabel ?? "",
            disabled: !!endpoint?.disabledAt,
          };
        }),
        ...detail.members.map((m) => ({
          kind: "email" as const,
          optionId: m.organizationMembershipId,
          optionLabel: m.email,
        })),
      ]
    : [];

  return {
    error: false,
    isEdit,
    form: {
      descriptors,
      initialName: detail?.name ?? "",
      initialTargets,
      rules: detail?.rules ?? [],
      isDefault: detail?.isDefault ?? false,
      canEdit: detail ? detail.permittedToEdit : true,
      canDelete: detail ? detail.permittedToDelete : false,
    },
  };
}

function AlertDestinationForm({
  organizationSlug,
  destinationId,
  initialName,
  initialTargets,
  rules,
  isDefault,
  descriptors,
  canEdit,
  linkRules,
  onCreated,
  onSaved,
  onCancel,
}: {
  organizationSlug: string;
  // Present when editing an existing destination (enables update + the rules
  // section); absent when creating a new one.
  destinationId?: string;
  initialName: string;
  initialTargets: Target[];
  rules: TargetRule[];
  isDefault: boolean;
  descriptors: Descriptor[];
  canEdit: boolean;
  // Render rule references as links
  linkRules: boolean;
  onCreated: (destination: { id: string; name: string }) => void;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { organizationIntegrations, organizationAlertRule } = useRoutes();
  const { email: currentUserEmail } = useCurrentUser();
  const [state, dispatch] = useReducer(
    (value: { name: string; targets: Target[] }, action: FormAction) => {
      switch (action.type) {
        case "rename":
          return { ...value, name: action.value };
        case "add":
          return { ...value, targets: value.targets.concat(action.target) };
        case "remove":
          return {
            ...value,
            targets: value.targets.filter(
              (t) =>
                !(
                  t.kind === action.target.kind &&
                  t.optionId === action.target.optionId
                ),
            ),
          };
      }
    },
    { name: initialName, targets: initialTargets },
  );

  const [createDestination, createResult] = useCreateAlertDestinationMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  const [updateDestination, updateResult] = useUpdateAlertDestinationMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  const [leaveDestination, leaveResult] = useLeaveAlertDestinationMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  useAsyncActionFlash({
    called: leaveResult.called,
    loading: leaveResult.loading,
    error: leaveResult.error?.message,
    success: "Removed your e-mail from this alert destination",
  });
  const left = leaveResult.data?.leaveAlertDestination;
  useEffect(() => {
    if (left) {
      onSaved();
    }
  }, [onSaved, left]);

  const saveResult = destinationId ? updateResult : createResult;
  useAsyncActionFlash({
    called: saveResult.called,
    loading: saveResult.loading,
    error: saveResult.error?.message,
    success: "Alert destination saved",
  });
  const created = createResult.data?.createAlertDestination;
  useEffect(() => {
    if (created) {
      onCreated(created);
    }
  }, [onCreated, created]);
  const updated = updateResult.data?.updateAlertDestination;
  useEffect(() => {
    if (updated) {
      onSaved();
    }
  }, [onSaved, updated]);

  function buildVariables() {
    const integrations = state.targets
      .filter((t) => t.kind !== "email")
      .map((t) => {
        if (ENDPOINT_KINDS.includes(t.kind)) {
          return { integrationEndpointId: t.optionId };
        }
        const descriptor = descriptors.find((d) => d.kind === t.kind);
        return {
          organizationIntegrationId: descriptor!.organizationIntegrationId!,
          targetId: t.optionId,
          targetLabel: t.optionLabel,
        };
      });
    const memberIds = state.targets
      .filter((t) => t.kind === "email")
      .map((t) => t.optionId);
    return { name: state.name, integrations, memberIds };
  }

  function handleSave() {
    const { name, integrations, memberIds } = buildVariables();
    if (destinationId) {
      updateDestination({
        variables: {
          alertDestinationId: destinationId,
          name,
          integrations,
          memberIds,
        },
      });
    } else {
      createDestination({
        variables: { organizationSlug, name, integrations, memberIds },
      });
    }
  }

  // Remove the current user as a recipient. Available to viewers without edit
  // permission (they get this instead of Save).
  function handleLeave() {
    if (!destinationId) {
      return;
    }
    leaveDestination({ variables: { alertDestinationId: destinationId } });
  }

  const saving = saveResult.loading || leaveResult.loading;
  // When editing, disable Save until something actually changes.
  const targetKey = (t: Target) => `${t.kind}:${t.optionId}`;
  const unchanged =
    !!destinationId &&
    state.name === initialName &&
    sameSet(state.targets.map(targetKey), initialTargets.map(targetKey));
  const saveDisabledReason =
    state.name.trim() === ""
      ? "Enter a name for this destination"
      : state.targets.length === 0
      ? "Add at least one target"
      : unchanged
      ? "No changes to save"
      : undefined;
  // A viewer without edit rights can't Save, but can remove themselves if they
  // are currently a recipient.
  const isRecipient = state.targets.some(
    (t) => t.kind === "email" && t.optionLabel === currentUserEmail,
  );

  return (
    <PanelSection>
      {isDefault && (
        <Callout variant="info" title="Default destination" className="mb-4">
          New organization members are automatically subscribed to this
          destination.
        </Callout>
      )}
      <label className="flex flex-col font-normal">
        <SectionHeading>
          Name <span className="text-[#c00]">*</span>
        </SectionHeading>
        <input
          className="form-control max-w-[600px]"
          required
          aria-required
          disabled={!canEdit}
          value={state.name}
          onChange={(e) =>
            dispatch({ type: "rename", value: e.currentTarget.value })
          }
        />
      </label>
      <div className="mt-4">
        <SectionHeading>Targets</SectionHeading>
        {SECTIONS.filter(
          // Unlike Slack/PagerDuty, an absent webhooks or incident.io section
          // means the feature is off, not that something can be connected: hide
          // it entirely
          (section) =>
            !ENDPOINT_KINDS.includes(section.kind) ||
            descriptors.some((d) => d.kind === section.kind),
        ).map((section) => {
          const descriptor = descriptors.find((d) => d.kind === section.kind);
          const targets = state.targets.filter((t) => t.kind === section.kind);
          return (
            <TargetSection
              key={section.kind}
              kind={section.kind}
              label={section.label}
              noun={section.noun}
              descriptor={descriptor}
              targets={targets}
              canEdit={canEdit}
              currentUserEmail={currentUserEmail}
              manageIntegrationsHref={organizationIntegrations(
                organizationSlug,
              )}
              onAdd={(target) => dispatch({ type: "add", target })}
              onRemove={(target) => dispatch({ type: "remove", target })}
            />
          );
        })}
      </div>
      {destinationId && (
        <div className="mt-4">
          <SectionHeading>Alert rules</SectionHeading>
          {rules.length === 0 ? (
            <div className="text-[13px] text-[#606771]">
              No alert rules route to this destination.
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <div className="text-[#595959]">
                This destination is referenced in the following alert rules:
              </div>
              {rules.map((rule) => (
                <div key={rule.id}>
                  {linkRules ? (
                    <Link to={organizationAlertRule(organizationSlug, rule.id)}>
                      {rule.name}
                    </Link>
                  ) : (
                    <span>{rule.name}</span>
                  )}
                  <span className="text-[#606771]">
                    {" "}
                    · {rule.server?.name ?? "All servers"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {canEdit ? (
        <div className="mt-4">
          <span
            title={saveDisabledReason}
            className={
              saveDisabledReason
                ? "inline-block cursor-not-allowed"
                : "inline-block"
            }
          >
            <Button
              bare
              disabled={!!saveDisabledReason || saving}
              className={
                saveDisabledReason
                  ? "btn btn-success pointer-events-none"
                  : "btn btn-success"
              }
              onClick={handleSave}
            >
              Save Alert Destination
            </Button>
          </span>
          <Button
            bare
            className="text-link hover:text-link-hover ml-3"
            disabled={saving}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      ) : (
        isRecipient && (
          <div className="mt-4">
            <p className="mb-2 text-[13px] text-[#606771]">
              You do not have permission to make other changes to this alert
              destination, but you can still remove your e-mail from it.
            </p>
            <Button
              bare
              className="btn btn-success"
              disabled={saving}
              onClick={handleLeave}
            >
              Leave Alert Destination
            </Button>
            <Button
              bare
              className="text-link hover:text-link-hover ml-3"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        )
      )}
    </PanelSection>
  );
}

function DeletePanel({
  destinationId,
  ruleCount,
  onDeleted,
}: {
  destinationId: string;
  ruleCount: number;
  onDeleted: () => void;
}) {
  const [deleteDestination, deleteResult] = useDeleteAlertDestinationMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  useAsyncActionFlash({
    called: deleteResult.called,
    loading: deleteResult.loading,
    error: deleteResult.error?.message,
    success: "Alert destination deleted",
  });
  const deleted = deleteResult.data?.deleteAlertDestination;
  useEffect(() => {
    if (deleted) {
      onDeleted();
    }
  }, [onDeleted, deleted]);

  function handleDelete() {
    const message =
      ruleCount > 0
        ? `This alert destination is referenced by ${pluralize(
            "alert rule",
            ruleCount,
            true,
          )}. Are you sure you want to delete it?`
        : "Are you sure you want to delete this alert destination?";
    if (window.confirm(message)) {
      deleteDestination({ variables: { alertDestinationId: destinationId } });
    }
  }

  return (
    <Panel title="Removal">
      <PanelSection>
        <p>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Deleting this destination removes it from every alert rule that uses
          it. Alerts will no longer be delivered to its Slack channels,
          PagerDuty services, or e-mail recipients.
        </p>
        <p>
          <span className="block form-group">
            <button
              className="btn btn-danger"
              disabled={deleteResult.loading}
              onClick={handleDelete}
            >
              Delete Destination
            </button>
          </span>
        </p>
      </PanelSection>
    </Panel>
  );
}

function TargetSection({
  kind,
  label,
  noun,
  descriptor,
  targets,
  canEdit,
  currentUserEmail,
  manageIntegrationsHref,
  onAdd,
  onRemove,
}: {
  kind: TargetKind;
  label: string;
  noun: string;
  descriptor?: Descriptor;
  targets: Target[];
  canEdit: boolean;
  currentUserEmail: string;
  manageIntegrationsHref: string;
  onAdd: (target: Target) => void;
  onRemove: (target: Target) => void;
}) {
  const Icon = KIND_ICONS[kind];
  const count = targets.length;
  const summary = `${count} ${noun}${count === 1 ? "" : "s"}`;
  // Show chips sorted alphabetically by label, regardless of the order targets
  // were added or returned in.
  const sortedTargets = [...targets].sort((a, b) =>
    a.optionLabel.localeCompare(b.optionLabel),
  );
  // Email always has a descriptor (org memberships); Slack/PagerDuty only when
  // the integration is connected. When disconnected we hide the count and the
  // chips: those targets can't fire without a connection.

  return (
    <div className="mt-3">
      <span className="flex items-center gap-1.5 font-bold text-[rgb(85,85,85)]">
        <Icon /> {label}
        {descriptor && (
          <span className="font-normal text-[#606771]">({summary})</span>
        )}
      </span>
      <div className="mt-1">
        {descriptor && count > 0 && (
          <div className="flex flex-wrap">
            {sortedTargets.map((target) => {
              const isSelf =
                kind === "email" && target.optionLabel === currentUserEmail;
              return (
                <TargetChip
                  key={target.optionId}
                  target={target}
                  highlight={isSelf}
                  onRemove={canEdit ? () => onRemove(target) : undefined}
                />
              );
            })}
          </div>
        )}
        {descriptor ? (
          canEdit && (
            <>
              <AddTarget
                descriptor={descriptor}
                currentTargets={targets}
                onAdd={onAdd}
              />
              {ENDPOINT_KINDS.includes(kind) && (
                <div className="mt-1 text-[13px] text-[#606771]">
                  {kind === "webhook"
                    ? "Webhook endpoints"
                    : "incident.io alert sources"}{" "}
                  are managed on the{" "}
                  <Link to={manageIntegrationsHref}>Integrations page</Link>.
                </div>
              )}
            </>
          )
        ) : (
          <div className="text-[13px] text-[#606771]">
            Not connected ·{" "}
            <Link to={manageIntegrationsHref}>Manage integrations</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function TargetChip({
  target,
  highlight,
  onRemove,
}: {
  target: Target;
  // Set when this target is the current user to visually distinguish the chip
  // so a user can spot themselves in the recipient list.
  highlight?: boolean;
  // Renders a trailing "×" to remove this target; omit to render a static chip.
  onRemove?: () => void;
}) {
  const [borderColor, background] = highlight
    ? ["border-[#66afe9]", "bg-[#e6f3fb]"]
    : target.disabled
    ? ["border-[#ccc]", "bg-[#f2f2f2]"]
    : ["border-[#ddd]", "bg-white"];
  return (
    <span
      title={highlight ? "This is your email address" : undefined}
      className={`mr-2 mt-[5px] inline-flex items-stretch rounded border align-middle ${borderColor} ${background}`}
    >
      <span className="inline-flex items-center px-[6px] py-[2px]">
        <span className="text-sm">
          {target.optionLabel}
          {target.disabled && <DisabledNote kind={target.kind} />}
        </span>
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${target.optionLabel}`}
          className={`inline-flex items-center border-l px-[6px] text-[#999] hover:text-[#c00] ${borderColor}`}
        >
          ×
        </button>
      )}
    </span>
  );
}

function DisabledNote({ kind }: { kind: TargetKind }) {
  const subject =
    kind === "incident_io"
      ? "This incident.io alert source"
      : "This webhook endpoint";
  return (
    <span
      title={`${subject} is disabled and will not receive alerts until re-enabled`}
      className="ml-1 text-[#c93a2a]"
    >
      <FontAwesomeIcon className="mr-1" icon={faExclamationTriangle} />
      disabled
    </span>
  );
}

// Add a single target within one kind: a searchable picker over the not-yet-
// selected options.
function AddTarget({
  descriptor,
  currentTargets,
  onAdd,
}: {
  descriptor: Descriptor;
  currentTargets: Target[];
  onAdd: (target: Target) => void;
}) {
  const noun = descriptor.targetLabel.toLowerCase();
  const available = descriptor.options.filter(
    (o) => !currentTargets.some((t) => t.optionId === o.id),
  );
  // Distinguish "there are none at all" from "you've already selected them all".
  const allSelected = descriptor.options.length > 0 && available.length === 0;
  const noneAvailable = available.length === 0;
  const emptyMessage = allSelected
    ? `No other ${noun}s available`
    : `No ${noun}s available`;
  return (
    <div className="mt-1 max-w-[600px]">
      <SingleSelect<Option>
        value={null}
        options={available}
        onChange={(option) =>
          option &&
          onAdd({
            kind: descriptor.kind,
            optionId: option.id,
            optionLabel: option.label,
            disabled: option.disabled,
          })
        }
        getOptionValue={(o) => o.id}
        // Plain label so typing still matches on the name alone
        getOptionLabel={(o) => o.label}
        formatOptionLabel={(o) => (
          <>
            {o.label}
            {o.disabled && <DisabledNote kind={descriptor.kind} />}
          </>
        )}
        // When nothing is left to add, surface the reason up front (as the
        // placeholder) and disable the picker rather than making the user open it.
        isDisabled={noneAvailable}
        placeholder={noneAvailable ? emptyMessage : `Add ${noun}…`}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? `No matches for "${inputValue}"` : emptyMessage
        }
        aria-label={`Add ${noun}`}
      />
    </div>
  );
}

export default AlertDestinationDetail;
